/**
 * @module design/goldenGeometry
 * @description Project-tier design tokens derived from the universal
 * `GOLDEN_MAJOR` constant (tier 1, from @jochantrelle/golden-ratio-splitter).
 * See ~/Dev/_docs/2026-07-18_SHARED_PACKAGES_SOP.md, "Tiered design tokens".
 *
 * Tier 1 (universal) : GOLDEN_MAJOR                    — package
 * Tier 1 (project)   : BASE_FONT_PX                    — this file, set once
 * Tier 2 (semantic)  : type scale, spacing scale       — derived here
 * Tier 3 (component) : consumed in CSS via var(--…)
 *
 * The derived values are written to CSS custom properties at boot, so there
 * is no hand-maintained parallel CSS copy to drift out of sync (the
 * `golden-geometry.css` hand-sync risk logged in DEV_UI_VISION).
 */
import { GOLDEN_MAJOR } from "@jochantrelle/golden-ratio-splitter/constants";

/** φ ≈ 1.618, derived rather than re-typed as a literal. */
export const PHI: number = 1 / GOLDEN_MAJOR;

/** Project primitive: the body text size the whole scale grows from. */
export const BASE_FONT_PX = 17;

/**
 * Modular scale step. Half-steps use √φ so the scale has enough rungs for
 * UI text while every value remains a power of φ.
 * @param step - Scale position; 0 = base, 2 = ×φ, 4 = ×φ², -2 = ×1/φ.
 */
export const phiStep = (step: number): number => Math.pow(Math.sqrt(PHI), step);

/** Named semantic tokens → [css variable, value]. */
export function buildGoldenTokens(): Record<string, string> {
    const rem = (n: number) => `${n.toFixed(4)}rem`;
    const type: Record<string, number> = {
        "--fs-xs": -2, "--fs-sm": -1, "--fs-base": 0, "--fs-md": 1,
        "--fs-lg": 2, "--fs-xl": 3, "--fs-2xl": 4, "--fs-3xl": 5, "--fs-4xl": 6, "--fs-5xl": 8,
    };
    const space: Record<string, number> = {
        "--sp-3xs": -4, "--sp-2xs": -3, "--sp-xs": -2, "--sp-sm": -1, "--sp-md": 0,
        "--sp-lg": 2, "--sp-xl": 4, "--sp-2xl": 6, "--sp-3xl": 8,
    };
    const tokens: Record<string, string> = {
        "--phi": PHI.toFixed(10),
        "--golden-major": `${(GOLDEN_MAJOR * 100).toFixed(4)}%`,
        "--golden-minor": `${((1 - GOLDEN_MAJOR) * 100).toFixed(4)}%`,
        // Grid tracks in fr units: percentages plus a gap would overflow the row.
        "--grid-major-minor": `minmax(0, ${PHI.toFixed(6)}fr) minmax(0, 1fr)`,
        "--grid-minor-major": `minmax(0, 1fr) minmax(0, ${PHI.toFixed(6)}fr)`,
        "--base-font": `${BASE_FONT_PX}px`,
        "--lh-body": PHI.toFixed(4),
        // Display lines: √φ (≈1.272) leading keeps large serif headings compact.
        "--lh-tight": phiStep(1).toFixed(4),
    };
    Object.entries(type).forEach(([k, s]) => (tokens[k] = rem(phiStep(s))));
    Object.entries(space).forEach(([k, s]) => (tokens[k] = rem(phiStep(s))));
    return tokens;
}

/** Writes the golden tokens onto :root. Called once before first render. */
export function applyGoldenTokens(root: HTMLElement = document.documentElement): void {
    Object.entries(buildGoldenTokens()).forEach(([k, v]) => root.style.setProperty(k, v));
}
